/**
 * Dashboard.js - 대시보드 페이지 (Manager 이상)
 * 투입시간 정보를 바탕으로 조직의 프로젝트별, 고객사별 업무 투입시간 시각화
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  InputAdornment,
} from '@mui/material';
import { BarChart2, Clock, Users, AlertCircle, TrendingDown, RefreshCw, MoreVertical, ChevronUp, ChevronDown } from 'react-feather';
import { FiClock, FiBriefcase, FiAlertCircle, FiTrendingUp } from 'react-icons/fi';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveTreeMap } from '@nivo/treemap';
import { ResponsiveTimeRange } from '@nivo/calendar';
import { eachDayOfInterval, parseISO, format, getDay, subDays } from 'date-fns';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import { useUserRole } from '../contexts/UserRoleContext';
// Dashboard는 운영환경 DB를 사용하므로 DBConnectionContext를 사용하지 않음
// 관리자 페이지의 DB 연결과는 별개로 작동해야 함
import KPICard from '../components/KPICard';
import DashboardDetailModal from '../components/DashboardDetailModal';
import EmployeeTreeSelect from '../components/EmployeeTreeSelect';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

// 담당자 이름에서 숫자 제거 함수 (예: "조흥제 123" => "조흥제")
const getEmployeeDisplayName = (employee) => {
  if (!employee) return '';
  const name = employee.employee_name || employee.employee_code || '';
  // 이름 뒤의 공백과 숫자 제거
  return name.replace(/\s+\d+$/, '').trim() || employee.employee_code || '';
};

const Dashboard = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const { isUser, isManager, isAdmin, isRoot, userRole } = useUserRole();
  // Dashboard는 항상 백엔드의 운영환경 DB를 사용하므로 연결 체크 불필요
  // 백엔드가 시작되면 자동으로 운영환경 DB에 연결됨
  
  // 기간 검색: from, to 날짜
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(1); // 이번 달 1일
    return date.toISOString().split('T')[0];
  };
  const getDefaultToDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  // 사용자 식별을 위한 employeeCode 가져오기 (가장 먼저 선언)
  const employeeCode = localStorage.getItem('employeeCode') || '';
  
  // 사용자별 대시보드 설정 저장/불러오기
  const loadDashboardSettings = useCallback(() => {
    try {
      const storageKey = `dashboard_settings_${employeeCode}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const settings = JSON.parse(saved);
        return settings;
      }
    } catch (error) {
      console.error('대시보드 설정 불러오기 실패:', error);
    }
    return null;
  }, [employeeCode]);

  const saveDashboardSettings = useCallback((settings) => {
    try {
      const storageKey = `dashboard_settings_${employeeCode}`;
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('대시보드 설정 저장 실패:', error);
    }
  }, [employeeCode]);

  // 저장된 설정 불러오기
  const savedSettings = useMemo(() => loadDashboardSettings(), [loadDashboardSettings]);

  const [fromDate, setFromDate] = useState(() => savedSettings?.fromDate || getDefaultFromDate());
  const [toDate, setToDate] = useState(() => savedSettings?.toDate || getDefaultToDate());
  const [customerHours, setCustomerHours] = useState([]);
  const [projectHours, setProjectHours] = useState([]);
  const [delayStats, setDelayStats] = useState([]);
  const [delayCount, setDelayCount] = useState(0); // 입력지연 건수 (직접 계산)
  const [timeInputCalendarData, setTimeInputCalendarData] = useState([]); // 입력시간 관리 calendar 데이터
  const [weekMinutesData, setWeekMinutesData] = useState(0); // 최근 7일간 누적 시간 (분 단위)
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // 새로고침 애니메이션 상태
  
  // 인원 필터 (글로벌 - 모든 섹션에 공통 적용)
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedEmployeeGlobal, setSelectedEmployeeGlobal] = useState(() => savedSettings?.selectedEmployees || []);
  
  // 고유 직원 수 계산 (겸직자 문제 해결)
  const uniqueEmployeeCount = useMemo(() => {
    if (!Array.isArray(selectedEmployeeGlobal)) return 0;
    const uniqueSet = new Set(selectedEmployeeGlobal);
    return uniqueSet.size;
  }, [selectedEmployeeGlobal]);
  
  // 고유 직원 코드 추출
  const uniqueEmployeeCodes = useMemo(() => {
    if (!Array.isArray(selectedEmployeeGlobal)) return [];
    return [...new Set(selectedEmployeeGlobal)];
  }, [selectedEmployeeGlobal]);
  
  // 접근 가능한 직원 목록 (권한별 필터링용)
  const [accessibleEmployeeCodes, setAccessibleEmployeeCodes] = useState(undefined); // undefined: 로딩 중, null: 모든 직원, []: 필터링됨
  const [isAccessibleEmployeesLoaded, setIsAccessibleEmployeesLoaded] = useState(false); // 접근 가능한 직원 목록 로딩 완료 여부
  
  // 모달 상태
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalType, setDetailModalType] = useState(null);
  
  // localStorage 키 생성 함수
  const getStorageKey = (chartType, empCode = null) => {
    const code = empCode || employeeCode;
    return `dashboard_chart_color_${chartType}_${code}`;
  };
  
  // 저장된 색상 팔레트 불러오기
  const loadSavedColorScheme = (chartType, defaultValue, empCode = null) => {
    try {
      const key = getStorageKey(chartType, empCode);
      const saved = localStorage.getItem(key);
      // 빈 문자열, null, 또는 잘못된 형식 체크
      if (saved && saved.trim() !== '' && 
          typeof saved === 'string' && 
          !saved.startsWith('{') && !saved.startsWith('[')) {
        // 유효한 색상 스킴 이름인지 간단히 체크 (알파벳, 숫자, 언더스코어만 허용)
        const trimmedSaved = saved.trim();
        if (/^[a-zA-Z0-9_]+$/.test(trimmedSaved)) {
          return trimmedSaved;
        } else {
          // 잘못된 값이면 localStorage에서 삭제하고 기본값 반환
          console.warn(`잘못된 색상 스킴 값 발견: ${trimmedSaved}, 기본값으로 재설정합니다.`);
          localStorage.removeItem(key);
          return defaultValue;
        }
      } else if (saved) {
        // 잘못된 형식의 값이면 localStorage에서 삭제
        console.warn(`잘못된 형식의 색상 스킴 값 발견, localStorage에서 삭제합니다.`);
        localStorage.removeItem(key);
      }
      return defaultValue;
    } catch (error) {
      console.error(`색상 팔레트 불러오기 실패 (${chartType}):`, error);
      return defaultValue;
    }
  };
  
  // 색상 팔레트 저장하기
  const saveColorScheme = (chartType, scheme, empCode = null) => {
    try {
      const key = getStorageKey(chartType, empCode);
      localStorage.setItem(key, scheme);
    } catch (error) {
      console.error(`색상 팔레트 저장 실패 (${chartType}):`, error);
    }
  };
  
  // 차트별 색상 팔레트 상태 (저장된 값으로 초기화)
  const [customerChartColorScheme, setCustomerChartColorScheme] = useState(() => {
    const scheme = loadSavedColorScheme('customer', 'category10');
    // 유효성 검사: 문자열이고 비어있지 않아야 함
    return (typeof scheme === 'string' && scheme.trim() !== '') ? scheme : 'category10';
  });
  const [projectChartColorScheme, setProjectChartColorScheme] = useState(() => {
    const scheme = loadSavedColorScheme('project', 'paired');
    // 유효성 검사: 문자열이고 비어있지 않아야 함
    return (typeof scheme === 'string' && scheme.trim() !== '') ? scheme : 'paired';
  });
  
  
  // 차트용 안전한 색상 스킴 값 (유효성 검사 포함)
  const safeCustomerColorScheme = useMemo(() => {
    const scheme = customerChartColorScheme;
    // 유효성 검사: 문자열이고 비어있지 않아야 함
    if (typeof scheme === 'string' && scheme.trim() !== '' && 
        !scheme.startsWith('{') && !scheme.startsWith('[') &&
        !scheme.startsWith('undefined') && !scheme.startsWith('null')) {
      return scheme.trim();
    }
    return 'category10';
  }, [customerChartColorScheme]);
  
  const safeProjectColorScheme = useMemo(() => {
    const scheme = projectChartColorScheme;
    // 유효성 검사: 문자열이고 비어있지 않아야 함
    if (typeof scheme === 'string' && scheme.trim() !== '' && 
        !scheme.startsWith('{') && !scheme.startsWith('[') &&
        !scheme.startsWith('undefined') && !scheme.startsWith('null')) {
      return scheme.trim();
    }
    return 'paired';
  }, [projectChartColorScheme]);
  
  // Bar Chart용 색상 배열 변환 함수 (scheme 기반 색상 팔레트)
  const getColorArrayFromScheme = useCallback((scheme) => {
    // Nivo 색상 스킴별 기본 색상 배열
    const schemeColors = {
      category10: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
      paired: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a'],
      set2: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854', '#ffd92f', '#e5c494', '#b3b3b3'],
      set3: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd'],
      blues: ['#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'],
      greens: ['#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#006d2c', '#00441b'],
      greys: ['#f7f7f7', '#d9d9d9', '#bdbdbd', '#969696', '#737373', '#525252', '#252525', '#000000'],
      purples: ['#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#54278f', '#3f007d'],
      blue_green: ['#e0f3db', '#ccebc5', '#a8ddb5', '#7bccc4', '#4eb3d3', '#2b8cbe', '#0868ac', '#084081'],
      green_blue: ['#f0f9e8', '#ccebc5', '#a8ddb5', '#7bccc4', '#43a2ca', '#2c7fb8', '#0868ac', '#084081'],
    };
    
    // 유효한 scheme이면 해당 색상 배열 반환, 아니면 기본값
    const validScheme = (typeof scheme === 'string' && scheme.trim() !== '') ? scheme.trim() : 'category10';
    return schemeColors[validScheme] || schemeColors.category10;
  }, []);
  
  // Bar Chart용 안전한 색상 배열
  const safeCustomerColorArray = useMemo(() => {
    return getColorArrayFromScheme(safeCustomerColorScheme);
  }, [safeCustomerColorScheme, getColorArrayFromScheme]);
  
  // 색상 팔레트 메뉴 상태
  const [customerColorMenuAnchor, setCustomerColorMenuAnchor] = useState(null);
  const [projectColorMenuAnchor, setProjectColorMenuAnchor] = useState(null);
  
  // 사용 가능한 색상 팔레트 옵션 (Bar, TreeMap용) - 전문적이고 깔끔한 느낌의 실용적인 옵션
  const colorSchemeOptions = [
    { value: 'category10', label: 'Category 10 (기본)' },
    { value: 'paired', label: 'Paired' },
    { value: 'set2', label: 'Set 2' },
    { value: 'set3', label: 'Set 3' },
    { value: 'blues', label: 'Blues' },
    { value: 'greens', label: 'Greens' },
    { value: 'greys', label: 'Greys' },
    { value: 'purples', label: 'Purples' },
    { value: 'blue_green', label: 'Blue Green' },
    { value: 'green_blue', label: 'Green Blue' },
  ];
  
  // 색상 팔레트 메뉴 핸들러
  const handleColorMenuOpen = (chartType, event) => {
    if (chartType === 'customer') {
      setCustomerColorMenuAnchor(event.currentTarget);
    } else if (chartType === 'project') {
      setProjectColorMenuAnchor(event.currentTarget);
    }
  };
  
  const handleColorMenuClose = (chartType) => {
    if (chartType === 'customer') {
      setCustomerColorMenuAnchor(null);
    } else if (chartType === 'project') {
      setProjectColorMenuAnchor(null);
    }
  };
  
  const handleColorSchemeChange = (chartType, scheme) => {
    // 유효성 검사: scheme이 유효한 문자열이어야 함
    if (!scheme || typeof scheme !== 'string' || scheme.trim() === '') {
      console.error(`유효하지 않은 색상 스킴: ${scheme}`);
      return;
    }
    const currentEmployeeCode = localStorage.getItem('employeeCode') || '';
    if (chartType === 'customer') {
      setCustomerChartColorScheme(scheme.trim());
      saveColorScheme('customer', scheme.trim(), currentEmployeeCode);
      handleColorMenuClose('customer');
    } else if (chartType === 'project') {
      setProjectChartColorScheme(scheme.trim());
      saveColorScheme('project', scheme.trim(), currentEmployeeCode);
      handleColorMenuClose('project');
    }
  };
  
  // 컴포넌트 마운트 시 저장된 색상 팔레트 설정 불러오기
  useEffect(() => {
    const currentEmployeeCode = localStorage.getItem('employeeCode') || '';
    if (currentEmployeeCode) {
      // 현재 사용자의 저장된 설정 불러오기
      const savedCustomerScheme = loadSavedColorScheme('customer', 'category10', currentEmployeeCode);
      const savedProjectScheme = loadSavedColorScheme('project', 'paired', currentEmployeeCode); // 기본값을 'paired'로 변경
      
      // 유효성 검사 후 저장된 값이 현재 상태와 다르면 업데이트
      if (savedCustomerScheme && 
          typeof savedCustomerScheme === 'string' && 
          savedCustomerScheme.trim() !== '' &&
          savedCustomerScheme !== customerChartColorScheme) {
        setCustomerChartColorScheme(savedCustomerScheme.trim());
      } else if (!customerChartColorScheme || customerChartColorScheme.trim() === '') {
        // 현재 값이 유효하지 않으면 기본값으로 재설정
        setCustomerChartColorScheme('category10');
      }
      
      if (savedProjectScheme && 
          typeof savedProjectScheme === 'string' && 
          savedProjectScheme.trim() !== '' &&
          savedProjectScheme !== projectChartColorScheme) {
        setProjectChartColorScheme(savedProjectScheme.trim());
      } else if (!projectChartColorScheme || projectChartColorScheme.trim() === '') {
        // 현재 값이 유효하지 않으면 기본값으로 재설정
        setProjectChartColorScheme('paired');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 컴포넌트 마운트 시 한 번만 실행
  
  // DB 변경 감지 및 데이터 새로고침
  useEffect(() => {
    const handleDbChanged = () => {
      // DB가 변경되면 대시보드 데이터 새로고침
      // DB 변경 감지 (디버깅 필요 시에만 주석 해제)
      // console.log('DB 변경 감지: 대시보드 데이터 새로고침');
      fetchDashboardData();
    };
    
    window.addEventListener('dbChanged', handleDbChanged);
    return () => {
      window.removeEventListener('dbChanged', handleDbChanged);
    };
  }, []); // 한 번만 등록
  
  // KPI 데이터 계산 (기간 필터 적용)
  // 업무시간: 선택된 기간 내 전체 투입시간 (customerHours에서 계산)
  const todayMinutes = React.useMemo(() => {
    return customerHours.reduce((sum, item) => sum + (item.total_minutes || 0), 0);
  }, [customerHours]);
  
  // 이번주 누적: 최근 7일간의 업무시간 누적 (weekMinutesData에서 계산)
  const weekMinutes = React.useMemo(() => {
    return weekMinutesData;
  }, [weekMinutesData]);
  
  // 진행중 프로젝트: 선택된 기간 내 투입시간이 있는 운용 중인 프로젝트 개수
  // 담당자 선택이 없으면 접근 가능한 모든 직원의 데이터를 자동 집계하여 계산
  // 각 프로젝트(contract_id)를 고유하게 카운트하여 프로젝트 개수만 계산
  // 예: 프로젝트 A, 프로젝트 B → 2개 (참여 인원 수와 무관)
  const activeProjects = React.useMemo(() => {
    if (!projectHours || projectHours.length === 0) {
      return 0;
    }
    
    // 각 프로젝트(contract_id)별로 고유하게 집계
    // 같은 프로젝트가 여러 기간(period)에 나타나도 한 번만 카운트
    const projectSet = new Set();
    
    projectHours.forEach(project => {
      const projectKey = project.contract_id; // contract_id만 사용 (기간 무관)
      if (projectKey) {
        projectSet.add(projectKey);
      }
    });
    
    // 고유한 프로젝트 개수 반환
    return projectSet.size;
  }, [projectHours]);
  
  // 입력지연 건수는 fetchDashboardData에서 직접 계산 (useState로 관리)

  // 접근 가능한 직원 목록 조회 (모든 사용자)
  const [accessibleEmployeeList, setAccessibleEmployeeList] = useState([]); // 접근 가능한 직원 코드 목록 (배열)

  // 직원 목록 조회 함수
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/employees`);
      const employeesData = response.data || [];
      
      // employee_code를 기준으로 중복 제거
      const uniqueEmployees = employeesData.reduce((acc, current) => {
        const existingIndex = acc.findIndex(emp => emp.employee_code === current.employee_code);
        if (existingIndex === -1) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      setEmployees(uniqueEmployees);
    } catch (error) {
      console.error('직원 목록 조회 실패:', error);
    }
  }, []);

  // 부서 목록 조회 함수
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/departments`);
      const departmentsData = response.data || [];
      setDepartments(departmentsData);
    } catch (error) {
      console.error('부서 목록 조회 실패:', error);
    }
  }, []);

  // 접근 가능한 직원 목록 조회 함수
  const fetchAccessibleEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/accessible-employees`);
      const accessibleData = response.data || {};
      const employeeCodes = accessibleData.employee_codes || [];
      
      // 접근 가능한 직원 목록 저장 (집계 계산에 사용)
      setAccessibleEmployeeList(employeeCodes);
      
      // Root 또는 Admin 권한인 경우 모든 직원 활성화 (null = 모든 직원 활성화)
      if (isRoot || isAdmin) {
        setAccessibleEmployeeCodes(null);
      } else {
        // scope_type이 'ALL'인 경우 null로 설정 (모든 직원 활성화)
        if (accessibleData.scope_type === 'ALL') {
          setAccessibleEmployeeCodes(null);
        } else {
          setAccessibleEmployeeCodes(employeeCodes);
        }
      }
      
      // 로딩 완료 표시 (이것이 useEffect를 트리거하여 fetchDashboardData가 자동으로 호출됨)
      setIsAccessibleEmployeesLoaded(true);
    } catch (error) {
      console.error('접근 가능한 직원 목록 조회 실패:', error);
      // 에러 시 본인만 접근 가능하도록 설정
      setAccessibleEmployeeCodes([employeeCode]);
      setAccessibleEmployeeList([employeeCode]);
      // 에러 시에도 로딩 완료 표시 (대시보드 데이터 로드 시도)
      setIsAccessibleEmployeesLoaded(true);
    }
  }, [isRoot, isAdmin, employeeCode]);

  // 직원 목록 및 부서 목록 조회 (Root, Admin, Manager만)
  // 접근 가능한 직원 목록도 함께 조회 (모든 사용자)
  useEffect(() => {
    const loadData = async () => {
      // 모든 사용자에 대해 접근 가능한 직원 목록 먼저 조회
      await fetchAccessibleEmployees();
      
      // Root, Admin, Manager만 직원 및 부서 목록 조회
      if (isRoot || isAdmin || isManager) {
        await Promise.all([
          fetchEmployees(),
          fetchDepartments()
        ]);
      }
      
      // 접근 가능한 직원 목록 로드 완료 후 대시보드 데이터 자동 로드
      // fetchAccessibleEmployees가 완료되면 isAccessibleEmployeesLoaded가 true가 되고,
      // 이는 fetchDashboardData의 useEffect를 트리거합니다.
    };
    
    loadData();
  }, [isRoot, isManager, isAdmin, employeeCode, fetchEmployees, fetchDepartments, fetchAccessibleEmployees]);

  // 대시보드 설정 자동 저장 (기간, 직원 필터 변경 시)
  useEffect(() => {
    // 초기 로딩 중이 아닐 때만 저장 (무한 루프 방지)
    if (!loading && employeeCode) {
      const settings = {
        fromDate,
        toDate,
        selectedEmployees: selectedEmployeeGlobal,
        lastUpdated: new Date().toISOString(),
      };
      saveDashboardSettings(settings);
    }
  }, [fromDate, toDate, selectedEmployeeGlobal, loading, employeeCode, saveDashboardSettings]);

  // EmployeeTreeSelect 데이터 새로고침 핸들러
  const handleDataRefresh = async (dataType) => {
    // 데이터 새로고침 요청 (디버깅 필요 시에만 주석 해제)
    // console.log(`[Dashboard] 데이터 새로고침 요청: ${dataType}`);
    try {
      if (dataType === 'departments' || dataType === 'both') {
        await fetchDepartments();
      }
      if (dataType === 'employees' || dataType === 'both') {
        await fetchEmployees();
      }
    } catch (error) {
      console.error('[Dashboard] 데이터 새로고침 실패:', error);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    // 접근 가능한 직원 목록이 아직 로드되지 않았으면 대기
    // (fetchAccessibleEmployees가 완료될 때까지 대기)
    if (!isAccessibleEmployeesLoaded) {
      // 접근 가능한 직원 목록 로딩 대기 (디버깅 필요 시에만 주석 해제)
      // console.log('[Dashboard] 접근 가능한 직원 목록 로딩 대기 중...');
      return;
    }
    
    setLoading(true);
    setRefreshing(true);
    try {
      // 날짜 범위를 일자 기준으로 전달 (YYYY-MM-DD)
      // 백엔드 API는 start_date, end_date 파라미터를 받아 일자 범위로 조회합니다.
      const baseParams = { start_date: fromDate, end_date: toDate };
      
      // 글로벌 담당자 선택: 모든 섹션에 공통 적용
      // Root, Admin, Manager는 타인 데이터 조회 가능
      // 복수 선택 시 모든 선택된 직원의 데이터를 집계
      const globalEmployeeCodes = (isRoot || isAdmin || isManager)
        ? (Array.isArray(selectedEmployeeGlobal) && selectedEmployeeGlobal.length > 0 
            ? selectedEmployeeGlobal.join(',') 
            : undefined)
        : undefined;
      
      // 모든 섹션에 동일한 담당자 필터 적용
      // 담당자 선택이 없으면 employee_codes 파라미터를 전달하지 않아 백엔드에서 접근 가능한 모든 직원의 데이터를 자동 집계
      // (Root/Admin/Manager는 모든 직원, User는 본인만)
      const customerParams = globalEmployeeCodes
        ? { ...baseParams, employee_codes: globalEmployeeCodes }
        : baseParams; // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
      
      // 진행중 프로젝트 계산을 위한 파라미터
      // 담당자 선택이 없으면 employee_codes 파라미터를 전달하지 않아 백엔드에서 접근 가능한 모든 직원의 데이터를 자동 집계
      // (Root/Admin/Manager는 모든 직원, User는 본인만)
      const projectParams = globalEmployeeCodes
        ? { ...baseParams, employee_codes: globalEmployeeCodes }
        : baseParams; // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
      
      // delay-stats API는 start_date와 end_date가 필수이므로 baseParams를 항상 포함
      // 담당자 선택이 없으면 employee_codes 파라미터를 전달하지 않아 백엔드에서 접근 가능한 모든 직원의 데이터를 자동 집계
      const delayParams = globalEmployeeCodes
        ? { ...baseParams, employee_codes: globalEmployeeCodes }
        : baseParams; // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
      
      // Dashboard는 항상 백엔드의 운영환경 DB를 사용함
      // 관리자 페이지의 DB 연결 상태와는 무관하게 작동
      // 모든 API를 동시에 호출하여 로딩 시간 최소화
      
      // 이번주 누적: 최근 7일간의 업무시간 계산
      const today = new Date();
      const sevenDaysAgo = subDays(today, 6); // 오늘 포함 7일
      const weekStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
      const weekEndDate = format(today, 'yyyy-MM-dd');
      
      // 이번주 누적용 파라미터
      const weekSummaryParams = {
        start_date: weekStartDate,
        end_date: weekEndDate
      };
      if (globalEmployeeCodes) {
        weekSummaryParams.employee_codes = globalEmployeeCodes;
      }
      
      // 입력지연 건수용 파라미터 (전체 기간)
      const delaySummaryParams = {
        start_date: fromDate,
        end_date: toDate
      };
      if (globalEmployeeCodes) {
        delaySummaryParams.employee_codes = globalEmployeeCodes;
      }
      
      // 모든 API를 동시에 호출 (Promise.all 사용)
      const [customerRes, projectRes, delayRes, weekSummaryRes, delaySummaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dashboard/customer-hours`, { params: customerParams }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/dashboard/project-hours`, { params: projectParams }).catch(() => ({ data: [] })),
        (isRoot || isManager || isAdmin) ? axios.get(`${API_BASE_URL}/dashboard/delay-stats`, { params: delayParams }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        axios.get(`${API_BASE_URL}/dashboard/week-summary`, { params: weekSummaryParams }).catch(() => ({ data: { total_minutes: 0, delay_count: 0 } })),
        axios.get(`${API_BASE_URL}/dashboard/week-summary`, { params: delaySummaryParams }).catch(() => ({ data: { total_minutes: 0, delay_count: 0 } })),
      ]);

      // 실제 데이터만 사용 (샘플 데이터 제거)
      setCustomerHours(customerRes.data || []);
      setProjectHours(projectRes.data || []);
      setDelayStats(delayRes.data || []);
      
      // 이번주 누적 시간 설정
      const weekSummary = weekSummaryRes.data || { total_minutes: 0, delay_count: 0 };
      setWeekMinutesData(weekSummary.total_minutes || 0);
      
      // 입력지연 건수 설정
      const delaySummary = delaySummaryRes.data || { total_minutes: 0, delay_count: 0 };
      setDelayCount(Math.round(delaySummary.delay_count || 0));

      // 입력시간 관리 calendar 데이터 생성 (모든 사용자 - 본인 데이터 조회)
      // 시작일과 종료일 모두 포함
      try {
        // 날짜 문자열 유효성 검사
        if (!fromDate || !toDate) {
          console.warn('[입력시간 관리] 날짜가 설정되지 않았습니다.');
          setTimeInputCalendarData([]);
          return;
        }
        
        // 날짜 문자열을 파싱하여 날짜 범위 생성 (시작일, 종료일 포함)
        const startDateObj = parseISO(fromDate);
        const endDateObj = parseISO(toDate);
        
        // 날짜 객체 유효성 검사
        if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
          console.error('[입력시간 관리] 잘못된 날짜 형식:', { fromDate, toDate });
          setTimeInputCalendarData([]);
          return;
        }
        
        // eachDayOfInterval은 시작일과 종료일을 포함하지만, 
        // ResponsiveTimeRange 차트가 정확히 표시하도록 시작일과 종료일을 명시적으로 확인
        const days = eachDayOfInterval({ start: startDateObj, end: endDateObj });
        
        // days 배열이 비어있는지 확인
        if (!days || days.length === 0) {
          console.warn('[입력시간 관리] 날짜 범위가 유효하지 않습니다.');
          setTimeInputCalendarData([]);
          return;
        }
        
        // 날짜 범위 확인 (디버깅 필요 시에만 주석 해제)
        // console.log('[입력시간 관리] 날짜 범위:', { fromDate, toDate, daysCount: days.length });

        // 선택된 담당자에 따라 조회할 employee_code 결정
        // 1) root/admin/manager가 특정 1인을 선택한 경우: 해당 인원의 데이터 조회
        // 2) user 권한인 경우: 접속자 본인의 데이터 조회
        let targetEmployeeCode;
        if (isRoot || isAdmin || isManager) {
          // root/admin/manager: 고유 직원이 1명인 경우에만 처리 (겸직자 지원)
          const uniqueEmployees = [...new Set(selectedEmployeeGlobal)];
          
          if (uniqueEmployees.length === 1) {
            targetEmployeeCode = uniqueEmployees[0];
          } else {
            // 담당자 선택이 없거나 여러 명 선택된 경우
            setTimeInputCalendarData([]);
            return;
          }
        } else if (isUser) {
          // user 권한: 접속자 본인의 데이터 조회
          targetEmployeeCode = employeeCode;
        } else {
          // 권한이 없는 경우
          setTimeInputCalendarData([]);
          return;
        }
        
        // targetEmployeeCode가 없으면 데이터 생성하지 않음
        if (!targetEmployeeCode) {
          setTimeInputCalendarData([]);
          return;
        }
        
        // 각 날짜별로 투입시간 데이터 조회
        const calendarDataPromises = days.map(async (day) => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const dayOfWeek = getDay(day); // 0: 일요일, 6: 토요일
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          try {
            // 해당 날짜의 timelog 조회
            // /dashboard/timelog-details API는 employee_codes 파라미터를 지원하여
            // manager/admin이 선택한 직원의 데이터를 정확히 조회할 수 있음
            const params = { 
              date: dayStr,
              employee_codes: targetEmployeeCode  // 선택한 직원 코드 전달
            };
            
            const response = await axios.get(`${API_BASE_URL}/dashboard/timelog-details`, { params }).catch((err) => {
              console.warn(`[입력시간 관리] API 호출 실패 (${dayStr}):`, err.message);
              return { data: [] };
            });
            const dayLogs = response.data || [];
            
            // API가 이미 올바른 직원의 데이터를 반환하므로 필터링 불필요
            const filteredLogs = dayLogs;
            
            // 주말 처리: 입력이 없으면 주말(회색), 입력이 있으면 해당 색깔
            if (filteredLogs.length === 0 || !filteredLogs.some(log => (log.input_minute || 0) > 0)) {
              const resultValue = isWeekend ? 3 : 2;
              return { day: dayStr, value: resultValue }; // 주말: 3(회색), 평일: 2(누락/빨강)
            }

            // 가장 최근 입력 시간 확인
            const latestLog = filteredLogs.reduce((latest, log) => {
              if (!latest) return log;
              const logCreatedAt = new Date(log.created_at || log.updated_at);
              const latestCreatedAt = new Date(latest.created_at || latest.updated_at);
              return logCreatedAt > latestCreatedAt ? log : latest;
            }, null);

            if (latestLog) {
              // 날짜만 비교 (시간 제외) - 정확한 당일/지연 판단
              // 업무일: dayStr (예: '2024-12-04')
              // 입력일: created_at에서 날짜 부분만 추출
              const logDateStr = dayStr;  // 업무일 (yyyy-MM-dd)
              const createdDateStr = format(parseISO(latestLog.created_at || latestLog.updated_at), 'yyyy-MM-dd');
              
              if (createdDateStr === logDateStr) {
                // 당일 입력 (업무일 00:00:00 ~ 23:59:59 사이 입력)
                return { day: dayStr, value: 0 }; // 당일 (녹색)
              } else {
                // 지연 입력 (업무일 D+1 이후 입력)
                return { day: dayStr, value: 1 }; // 지연 (주황색)
              }
            }
            
            const resultValue = isWeekend ? 3 : 2;
            return { day: dayStr, value: resultValue }; // 주말: 3(회색), 평일: 2(누락)
          } catch (error) {
            console.error(`[입력시간 관리] ${dayStr} 처리 중 에러:`, error);
            return { day: dayStr, value: isWeekend ? 3 : 2 }; // 주말: 3(회색), 평일: 2(누락)
          }
        });

        const calendarData = await Promise.all(calendarDataPromises);
        
        // 시작일과 종료일이 데이터에 포함되어 있는지 확인
        const firstDay = format(startDateObj, 'yyyy-MM-dd');
        const lastDay = format(endDateObj, 'yyyy-MM-dd');
        const hasFirstDay = calendarData.some(item => item.day === firstDay);
        const hasLastDay = calendarData.some(item => item.day === lastDay);
        
        // 시작일과 종료일이 없으면 추가 (주말 여부 확인)
        if (!hasFirstDay && firstDay) {
          try {
            const firstDayObj = parseISO(firstDay);
            if (!isNaN(firstDayObj.getTime())) {
              const firstDayOfWeek = getDay(firstDayObj);
              const isFirstWeekend = firstDayOfWeek === 0 || firstDayOfWeek === 6;
              calendarData.unshift({ day: firstDay, value: isFirstWeekend ? 3 : 2 }); // 주말: 3(회색), 평일: 2(누락)
              // 시작일 추가 (디버깅 필요 시에만 주석 해제)
              // console.log('[입력시간 관리] 시작일 추가:', firstDay, isFirstWeekend ? '주말' : '평일');
            }
          } catch (error) {
            console.error('[입력시간 관리] 시작일 파싱 실패:', error);
          }
        }
        if (!hasLastDay && lastDay) {
          try {
            const lastDayObj = parseISO(lastDay);
            if (!isNaN(lastDayObj.getTime())) {
              const lastDayOfWeek = getDay(lastDayObj);
              const isLastWeekend = lastDayOfWeek === 0 || lastDayOfWeek === 6;
              calendarData.push({ day: lastDay, value: isLastWeekend ? 3 : 2 }); // 주말: 3(회색), 평일: 2(누락)
              // 종료일 추가 (디버깅 필요 시에만 주석 해제)
              // console.log('[입력시간 관리] 종료일 추가:', lastDay, isLastWeekend ? '주말' : '평일');
            }
          } catch (error) {
            console.error('[입력시간 관리] 종료일 파싱 실패:', error);
          }
        }
        
        // 날짜 순서대로 정렬
        calendarData.sort((a, b) => a.day.localeCompare(b.day));
        
        setTimeInputCalendarData(calendarData);
      } catch (error) {
        console.error('입력시간 관리 calendar 데이터 생성 실패:', error);
        setTimeInputCalendarData([]);
      }
    } catch (error) {
      console.error('대시보드 데이터 조회 실패:', error);
    } finally {
      setLoading(false);
      // 새로고침 애니메이션을 위해 약간의 지연 후 상태 해제
      setTimeout(() => {
        setRefreshing(false);
      }, 500);
    }
  }, [fromDate, toDate, selectedEmployeeGlobal, isRoot, isAdmin, isManager, isAccessibleEmployeesLoaded]);

  // 대시보드 데이터 자동 로딩 (컴포넌트 마운트 시 및 의존성 변경 시)
  // 접근 가능한 직원 목록이 로드된 후에만 실행
  useEffect(() => {
    if (isAccessibleEmployeesLoaded) {
      // 초기 로딩 시 모든 데이터를 동시에 로드
      // 약간의 지연을 두어 다른 초기화 작업이 완료되도록 함
      const timer = setTimeout(() => {
        fetchDashboardData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fetchDashboardData, isAccessibleEmployeesLoaded]);

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  // 날짜 증감 유틸리티
  const adjustDateByDays = (dateStr, delta) => {
    const baseDate = dateStr ? new Date(dateStr) : new Date();
    if (Number.isNaN(baseDate.getTime())) return dateStr || format(new Date(), 'yyyy-MM-dd');
    baseDate.setDate(baseDate.getDate() + delta);
    return format(baseDate, 'yyyy-MM-dd');
  };

  const handleRangeDateStep = (target, delta) => {
    const currentDate = target === 'from' ? fromDate : toDate;
    const newDate = adjustDateByDays(currentDate, delta);
    if (target === 'from') {
      setFromDate(newDate);
    } else {
      setToDate(newDate);
    }
  };

  // 모달 핸들러
  const handleKPIClick = (type) => {
    setDetailModalType(type);
    setDetailModalOpen(true);
  };

  const handleModalClose = () => {
    setDetailModalOpen(false);
    setDetailModalType(null);
  };

  return (
    <Box sx={{ p: 1.5, pt: 3 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h1" sx={{ color: colors.primaryText }}>
            대시보드
          </Typography>
          <Tooltip title="새로고침">
            <span>
              <IconButton
                onClick={() => fetchDashboardData()}
                size="small"
                disabled={loading}
                sx={{
                  color: colors.primaryText,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                <RefreshCw 
                  size={20} 
                  style={{
                  transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 0.5s ease-in-out',
                }}
              />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* 글로벌 담당자 선택 (Root, Admin, Manager만 표시) */}
          {(isRoot || isAdmin || isManager) && (
            <EmployeeTreeSelect
              employees={employees}
              departments={departments}
              selectedEmployees={Array.isArray(selectedEmployeeGlobal) ? selectedEmployeeGlobal : (selectedEmployeeGlobal ? [selectedEmployeeGlobal] : [])}
              onSelectionChange={(selected) => {
                // 다중 선택 모드: 선택된 모든 직원 저장
                setSelectedEmployeeGlobal(selected);
              }}
              onDataRefresh={handleDataRefresh}
              placeholder="담당자 선택"
              multiple={true}
              allowedEmployeeCodes={accessibleEmployeeCodes} // null: 모든 직원 활성화, []: 모두 비활성화, [codes]: 해당 직원만 활성화
            />
          )}
          <TextField
            type="date"
            label="시작일"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRangeDateStep('from', 1)}
                      sx={{ p: 0.25, color: colors.secondaryText }}
                      aria-label="시작일 하루 증가"
                    >
                      <ChevronUp size={14} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRangeDateStep('from', -1)}
                      sx={{ p: 0.25, color: colors.secondaryText }}
                      aria-label="시작일 하루 감소"
                    >
                      <ChevronDown size={14} />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 160,
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: isDark ? 'invert(1)' : 'none',
                opacity: 1,
                cursor: 'pointer',
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                opacity: 0.7,
              },
            }}
            inputProps={{
              sx: {
                color: colors.primaryText,
                '&::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                },
              }
            }}
          />
          <Typography variant="body2" sx={{ color: colors.secondaryText }}>
            ~
          </Typography>
          <TextField
            type="date"
            label="종료일"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRangeDateStep('to', 1)}
                      sx={{ p: 0.25, color: colors.secondaryText }}
                      aria-label="종료일 하루 증가"
                    >
                      <ChevronUp size={14} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRangeDateStep('to', -1)}
                      sx={{ p: 0.25, color: colors.secondaryText }}
                      aria-label="종료일 하루 감소"
                    >
                      <ChevronDown size={14} />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 160,
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: isDark ? 'invert(1)' : 'none',
                opacity: 1,
                cursor: 'pointer',
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                opacity: 0.7,
              },
            }}
            inputProps={{
              sx: {
                color: colors.primaryText,
                '&::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                },
              }
            }}
          />
        </Box>
      </Box>

      {/* KPI 카드 섹션 */}
      <Grid 
        container 
        spacing={{ xs: 3, sm: 3, md: 0 }}
        sx={{ 
          mb: 3, 
          flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
          justifyContent: { md: 'space-between' },
          gap: { md: 3 },
          '& > .MuiGrid-item': {
            '&:last-child': {
              marginRight: { md: 0 }
            }
          }
        }}
      >
        <Grid item xs={12} sm={6} md="auto" sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <KPICard
            value={Math.floor(todayMinutes / 60)}
            label="업무시간"
            icon={FiClock}
            unit="시간"
            theme={isDark ? 'dark' : 'light'}
            color="#00BFFF"
            trend={null}
            onClick={() => handleKPIClick('today')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md="auto" sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <KPICard
            value={Math.floor(weekMinutes / 60)}
            label="최근 7일 누적"
            icon={FiTrendingUp}
            unit="시간"
            theme={isDark ? 'dark' : 'light'}
            color="#66BB6A"
            trend={null}
            onClick={() => handleKPIClick('week')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md="auto" sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <KPICard
            value={activeProjects}
            label="프로젝트 건수"
            icon={FiBriefcase}
            unit="개"
            theme={isDark ? 'dark' : 'light'}
            color="#FF9800"
            trend={null}
            onClick={() => handleKPIClick('projects')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md="auto" sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <KPICard
            value={delayCount}
            label="입력지연 건수"
            icon={FiAlertCircle}
            unit="건"
            theme={isDark ? 'dark' : 'light'}
            color="#FF5252"
            trend={null}
            onClick={() => handleKPIClick('delays')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* 고객별 투입시간 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChart2 size={24} color={colors.accent} style={{ marginRight: 8 }} />
                  <Typography variant="h2" sx={{ fontSize: '18px' }}>고객별 투입시간</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {isUser && (
                    <Chip 
                      label="본인 기준 집계시간" 
                      size="small" 
                      color="info"
                      sx={{ fontSize: '11px' }}
                    />
                  )}
                  <Tooltip title="색상 팔레트 선택">
                    <IconButton
                      size="small"
                      onClick={(e) => handleColorMenuOpen('customer', e)}
                      sx={{ color: colors.secondaryText }}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={customerColorMenuAnchor}
                    open={Boolean(customerColorMenuAnchor)}
                    onClose={() => handleColorMenuClose('customer')}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        backgroundColor: isDark ? '#2A2B30' : '#FFFFFF',
                        border: `1px solid ${colors.primaryBorder}`,
                      }
                    }}
                  >
                    {colorSchemeOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        selected={customerChartColorScheme === option.value}
                        onClick={() => handleColorSchemeChange('customer', option.value)}
                        sx={{
                          color: colors.primaryText,
                          '&.Mui-selected': {
                            backgroundColor: colors.hoverBg,
                          },
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
              <Box sx={{ height: 400 }}>
                {customerHours.length > 0 ? (() => {
                  // 월별 고객별 데이터 변환
                  const periodMap = new Map();
                  const customerSet = new Set();
                  
                  // 데이터 수집
                  customerHours.forEach(item => {
                    const period = item.period || '기타';
                    const customer = item.company_name_kr || item.company_code || '미지정';
                    const hours = Math.round((item.total_minutes || 0) / 60 * 10) / 10;
                    
                    customerSet.add(customer);
                    
                    if (!periodMap.has(period)) {
                      periodMap.set(period, { period });
                    }
                    periodMap.get(period)[customer] = hours;
                  });
                  
                  // 배열로 변환 및 정렬
                  const chartData = Array.from(periodMap.values())
                    .sort((a, b) => a.period.localeCompare(b.period));
                  
                  // 고객사 목록 (keys)
                  const customerKeys = Array.from(customerSet).sort();
                  
                  // 커스텀 툴팁 컴포넌트
                  const CustomTooltip = ({ id, value, indexValue, color }) => {
                    // id는 고객사명, indexValue는 월(period)
                    return (
                      <Box
                        sx={{
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#2A2B30' : '#FFFFFF',
                          border: `1px solid ${colors.primaryBorder}`,
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.primaryText,
                            fontWeight: 600,
                            fontSize: '13px',
                            marginBottom: '4px',
                          }}
                        >
                          {id}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.secondaryText,
                            fontSize: '11px',
                            display: 'block',
                            marginBottom: '2px',
                          }}
                        >
                          월: {indexValue}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: color,
                            fontWeight: 600,
                            fontSize: '12px',
                          }}
                        >
                          투입시간: {value}시간
                        </Typography>
                      </Box>
                    );
                  };

                  return (
                    <ResponsiveBar
                      data={chartData}
                      keys={customerKeys}
                      indexBy="period"
                      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={safeCustomerColorArray}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: '월 (period)',
                        legendPosition: 'middle',
                        legendOffset: 32,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: '투입시간 (시간)',
                        legendPosition: 'middle',
                        legendOffset: -40,
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      tooltip={CustomTooltip}
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 120,
                          translateY: 0,
                          itemsSpacing: 3,
                          itemWidth: 100,
                          itemHeight: 16,
                          itemDirection: 'left-to-right',
                          itemOpacity: 0.85,
                          symbolSize: 12,
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemOpacity: 1
                              }
                            }
                          ]
                        }
                      ]}
                      theme={{
                        text: {
                          fill: colors.primaryText,
                          fontSize: 14, // 12 -> 14 (2px 증가)
                        },
                        axis: {
                          domain: {
                            line: {
                              stroke: colors.primaryBorder,
                            },
                          },
                          ticks: {
                            line: {
                              stroke: colors.primaryBorder,
                            },
                            text: {
                              fill: colors.secondaryText,
                            },
                          },
                        },
                        grid: {
                          line: {
                            stroke: colors.primaryBorder,
                            strokeWidth: 1,
                          },
                        },
                        legends: {
                          text: {
                            fill: colors.secondaryText,
                            fontSize: 13, // 11 -> 13 (2px 증가)
                          },
                        },
                      }}
                    />
                  );
                })() : (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <BarChart2 size={48} color={colors.secondaryText} />
                    <Typography variant="body1" sx={{ color: colors.secondaryText, fontWeight: 500 }}>
                      데이터가 없습니다
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.tertiaryText }}>
                      기준정보를 등록하고 투입시간을 입력해주세요
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 계약별 누적시간 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Clock size={24} color={colors.accent} style={{ marginRight: 8 }} />
                  <Typography variant="h2" sx={{ fontSize: '18px' }}>계약별 누적시간</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {isUser && (
                    <Chip 
                      label="본인 기준 집계시간" 
                      size="small" 
                      color="info"
                      sx={{ fontSize: '11px' }}
                    />
                  )}
                  <Tooltip title="색상 팔레트 선택">
                    <IconButton
                      size="small"
                      onClick={(e) => handleColorMenuOpen('project', e)}
                      sx={{ color: colors.secondaryText }}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={projectColorMenuAnchor}
                    open={Boolean(projectColorMenuAnchor)}
                    onClose={() => handleColorMenuClose('project')}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        backgroundColor: isDark ? '#2A2B30' : '#FFFFFF',
                        border: `1px solid ${colors.primaryBorder}`,
                      }
                    }}
                  >
                    {colorSchemeOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        selected={projectChartColorScheme === option.value}
                        onClick={() => handleColorSchemeChange('project', option.value)}
                        sx={{
                          color: colors.primaryText,
                          '&.Mui-selected': {
                            backgroundColor: colors.hoverBg,
                          },
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
              <Box sx={{ height: 500 }}>
                {projectHours.length > 0 ? (() => {
                  // 고객사별 계약별 데이터 변환 (treemap 계층 구조)
                  const companyMap = new Map();
                  
                  // 데이터 수집: 고객사 > 계약 계층 구조
                  projectHours.forEach(item => {
                    const companyName = item.company_name_kr || item.company_code || '미지정 고객사';
                    const contractTitle = item.contract_title || item.contract_id || '미지정 계약';
                    const hours = Math.round((item.total_minutes || 0) / 60 * 10) / 10;
                    
                    if (!companyMap.has(companyName)) {
                      companyMap.set(companyName, {
                        name: companyName,
                        children: [],
                      });
                    }
                    
                    // 계약별로 시간 합산 (같은 계약이 여러 월에 있을 수 있음)
                    const company = companyMap.get(companyName);
                    const existingContract = company.children.find(c => c.name === contractTitle);
                    if (existingContract) {
                      existingContract.loc += hours;
                    } else {
                      company.children.push({
                        name: contractTitle,
                        loc: hours,
                      });
                    }
                  });
                  
                  // 배열로 변환
                  const treeMapData = {
                    name: '계약별 누적시간',
                    children: Array.from(companyMap.values()),
                  };
                  
                  // RGB 색상의 밝기를 계산하는 함수 (0-255 범위)
                  const getLuminance = (color) => {
                    if (!color) return 128; // 기본값: 중간 밝기
                    
                    // hex 색상을 RGB로 변환
                    let hex = String(color).replace('#', '').trim();
                    
                    // 3자리 hex 색상 처리 (예: #FFF -> #FFFFFF)
                    if (hex.length === 3) {
                      hex = hex.split('').map(char => char + char).join('');
                    }
                    
                    // 6자리 hex 색상이 아니면 기본값 반환
                    if (hex.length !== 6) {
                      return 128;
                    }
                    
                    try {
                      const r = parseInt(hex.slice(0, 2), 16);
                      const g = parseInt(hex.slice(2, 4), 16);
                      const b = parseInt(hex.slice(4, 6), 16);
                      // 상대적 밝기 계산 (0.299*R + 0.587*G + 0.114*B)
                      return (0.299 * r + 0.587 * g + 0.114 * b);
                    } catch (error) {
                      return 128; // 오류 시 기본값
                    }
                  };
                  
                  // 배경색의 밝기에 따라 검은색 또는 흰색 텍스트 반환
                  const getTextColor = (backgroundColor) => {
                    // 다크모드에서는 항상 흰색 텍스트 사용
                    if (isDark) return '#FFFFFF';
                    
                    // 라이트모드에서는 항상 검은색 텍스트 사용
                    return '#000000';
                  };
                  
                  // 커스텀 툴팁 컴포넌트
                  const CustomTooltip = ({ node }) => {
                    return (
                      <Box
                        sx={{
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#2A2B30' : '#FFFFFF',
                          border: `1px solid ${colors.primaryBorder}`,
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.primaryText,
                            fontWeight: 600,
                            fontSize: '13px',
                            marginBottom: '4px',
                          }}
                        >
                          {node.data.name}
                        </Typography>
                        {node.depth === 1 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.secondaryText,
                              fontSize: '11px',
                              display: 'block',
                              marginBottom: '2px',
                            }}
                          >
                            고객사
                          </Typography>
                        )}
                        {node.depth === 2 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.secondaryText,
                              fontSize: '11px',
                              display: 'block',
                              marginBottom: '2px',
                            }}
                          >
                            계약
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: node.color,
                            fontWeight: 600,
                            fontSize: '12px',
                          }}
                        >
                          투입시간: {node.formattedValue}시간
                        </Typography>
                      </Box>
                    );
                  };
                  
                  return (
                    <ResponsiveTreeMap
                      data={treeMapData}
                      identity="name"
                      value="loc"
                      valueFormat=".1f"
                      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                      labelSkipSize={12}
                      labelTextColor={(node) => getTextColor(node.color)}
                      parentLabelPosition="left"
                      parentLabelTextColor={(node) => getTextColor(node.color)}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.1]] }}
                      colors={{ scheme: safeProjectColorScheme }}
                      tooltip={CustomTooltip}
                      theme={{
                        text: {
                          fill: colors.primaryText,
                          fontSize: 13, // 11 -> 13 (2px 증가)
                        },
                        labels: {
                          text: {
                            fill: colors.primaryText,
                            fontSize: 13, // 11 -> 13 (2px 증가)
                            fontWeight: 600,
                          },
                        },
                      }}
                    />
                  );
                })() : (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Clock size={48} color={colors.secondaryText} />
                    <Typography variant="body1" sx={{ color: colors.secondaryText, fontWeight: 500 }}>
                      데이터가 없습니다
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.tertiaryText }}>
                      기준정보를 등록하고 투입시간을 입력해주세요
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 입력시간 관리 
            1) root/admin/manager가 특정 1인을 선택한 경우: 해당 인원의 캘린더 차트 표시
            2) user 권한인 경우: 접속자 본인의 캘린더 차트 표시
            [개선] 겸직자를 고려하여 고유 직원 수로 판단
        */}
        {(((isRoot || isAdmin || isManager) && uniqueEmployeeCount === 1) || (isUser && employeeCode)) && (
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: isDark ? '#1C1D22' : '#FFFFFF' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AlertCircle size={24} color={colors.accent} style={{ marginRight: 8 }} />
                  <Typography variant="h2" sx={{ color: colors.primaryText, fontSize: '18px' }}>
                    입력시간 관리
                  </Typography>
                </Box>
              </Box>
                <Box sx={{ height: 400, width: '100%' }}>
                  {loading ? (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <Typography variant="body1" sx={{ color: colors.secondaryText, fontWeight: 500 }}>
                        데이터를 불러오는 중...
                      </Typography>
                    </Box>
                  ) : timeInputCalendarData.length > 0 ? (
                    <ResponsiveTimeRange
                      data={timeInputCalendarData}
                      from={(() => {
                        try {
                          if (!fromDate) {
                            const defaultDate = new Date();
                            defaultDate.setDate(1);
                            return format(subDays(defaultDate, 1), 'yyyy-MM-dd');
                          }
                          const parsedDate = parseISO(fromDate);
                          if (isNaN(parsedDate.getTime())) {
                            console.error('[입력시간 관리] 잘못된 fromDate:', fromDate);
                            const defaultDate = new Date();
                            defaultDate.setDate(1);
                            return format(subDays(defaultDate, 1), 'yyyy-MM-dd');
                          }
                          return format(subDays(parsedDate, 1), 'yyyy-MM-dd');
                        } catch (error) {
                          console.error('[입력시간 관리] fromDate 포맷팅 실패:', error);
                          const defaultDate = new Date();
                          defaultDate.setDate(1);
                          return format(subDays(defaultDate, 1), 'yyyy-MM-dd');
                        }
                      })()}
                      to={(() => {
                        try {
                          if (!toDate) {
                            return new Date().toISOString().split('T')[0];
                          }
                          const parsedDate = parseISO(toDate);
                          if (isNaN(parsedDate.getTime())) {
                            console.error('[입력시간 관리] 잘못된 toDate:', toDate);
                            return new Date().toISOString().split('T')[0];
                          }
                          return toDate;
                        } catch (error) {
                          console.error('[입력시간 관리] toDate 처리 실패:', error);
                          return new Date().toISOString().split('T')[0];
                        }
                      })()}
                      emptyColor={isDark ? '#2A2B30' : '#eeeeee'}
                      colors={['#4CAF50', '#FF9800', '#F44336', '#9E9E9E']}
                      domain={[0, 3]}
                      margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                      dayBorderWidth={2}
                      dayBorderColor={isDark ? '#1C1D22' : '#ffffff'}
                      theme={{
                        text: {
                          fill: colors.primaryText,
                          fontSize: 12,
                        },
                      }}
                      tooltip={(props) => {
                        // ResponsiveTimeRange의 tooltip props 형식 확인
                        // 디버깅: props 구조 확인 (개발 중에만 사용)
                        // Tooltip props 확인 (디버깅 필요 시에만 주석 해제)
                        // if (process.env.NODE_ENV === 'development') {
                        //   console.log('[입력시간 관리] Tooltip props:', props);
                        // }
                        
                        // props는 { day, value, color } 형식일 수 있음
                        let dayStr = '';
                        let dayValue = 2; // 기본값: 누락
                        
                        // day 형식 확인 및 변환
                        if (props.day) {
                          // day가 Date 객체일 수 있음
                          if (props.day instanceof Date) {
                            if (!isNaN(props.day.getTime())) {
                              try {
                                dayStr = format(props.day, 'yyyy-MM-dd');
                              } catch (error) {
                                console.warn('[입력시간 관리] Tooltip: 날짜 포맷팅 실패:', error, props.day);
                                dayStr = '';
                              }
                            } else {
                              console.warn('[입력시간 관리] Tooltip: 잘못된 Date 객체:', props.day);
                              dayStr = '';
                            }
                          } else if (typeof props.day === 'string') {
                            dayStr = props.day;
                          } else {
                            dayStr = String(props.day);
                          }
                        } else if (props.date) {
                          if (typeof props.date === 'string') {
                            dayStr = props.date;
                          } else if (props.date instanceof Date) {
                            if (!isNaN(props.date.getTime())) {
                              try {
                                dayStr = format(props.date, 'yyyy-MM-dd');
                              } catch (error) {
                                console.warn('[입력시간 관리] Tooltip: 날짜 포맷팅 실패:', error, props.date);
                                dayStr = '';
                              }
                            } else {
                              console.warn('[입력시간 관리] Tooltip: 잘못된 date:', props.date);
                              dayStr = '';
                            }
                          } else {
                            console.warn('[입력시간 관리] Tooltip: 알 수 없는 date 형식:', props.date);
                            dayStr = '';
                          }
                        }
                        
                        // 데이터에서 해당 날짜의 value 찾기
                        if (dayStr) {
                          const dayData = timeInputCalendarData.find(item => item.day === dayStr);
                          if (dayData) {
                            dayValue = dayData.value;
                          } else if (props.value !== undefined && props.value !== null) {
                            dayValue = props.value;
                          }
                        } else if (props.value !== undefined && props.value !== null) {
                          dayValue = props.value;
                        }
                        
                        // 최종 값 확인 (디버깅 필요 시에만 주석 해제)
                        // if (process.env.NODE_ENV === 'development') {
                        //   console.log('[입력시간 관리] Tooltip 계산:', { dayStr, dayValue, propsValue: props.value });
                        // }
                        
                        let status = '';
                        let color = '';
                        
                        if (dayValue === 0) {
                          status = '당일';
                          color = '#4CAF50';
                        } else if (dayValue === 1) {
                          status = '지연';
                          color = '#FF9800';
                        } else if (dayValue === 3) {
                          status = '주말';
                          color = '#9E9E9E';
                        } else {
                          status = '누락';
                          color = '#F44336';
                        }
                        
                        return (
                          <Box
                            sx={{
                              padding: '8px 12px',
                              backgroundColor: isDark ? '#2A2B30' : '#FFFFFF',
                              border: `1px solid ${colors.primaryBorder}`,
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.primaryText,
                                fontWeight: 600,
                                fontSize: '13px',
                                marginBottom: '4px',
                              }}
                            >
                              {dayStr || '날짜 정보 없음'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: color,
                                fontWeight: 600,
                                fontSize: '12px',
                              }}
                            >
                              {status}
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                  ) : (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <AlertCircle size={48} color={colors.secondaryText} />
                      <Typography variant="body1" sx={{ color: colors.secondaryText, fontWeight: 500 }}>
                        데이터가 없습니다
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.tertiaryText }}>
                        기준정보를 등록하고 투입시간을 입력해주세요
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>

      {/* 상세 정보 모달 */}
      <DashboardDetailModal
        open={detailModalOpen}
        onClose={handleModalClose}
        type={detailModalType}
        dateRange={{ fromDate, toDate }}
        selectedEmployees={selectedEmployeeGlobal}
      />
    </Box>
  );
};

export default Dashboard;
